import React from 'react';
import BrandLogo from '../BrandLogo';
import loadingStyles from '../shared/Loading.module.css';
import config from '../../config';
import styles from '../AdminCurriculumVideo.module.css';

export const SLOT_COUNT = 10;
export const TITLE_MAX_LENGTH = 100;
export const FEEDBACK_DURATION = { success: 3000, error: 5000 };
export const SLOT_POSITIONS = Array.from({ length: SLOT_COUNT }, (_, index) => index + 1);

export const EMPTY_SLOT_UI = Object.freeze({
  selectedFile: null,
  previewUrl: null,
  fileInputKey: 0,
  uploading: false,
  uploadFeedback: null,
  updatingTitle: false,
  titleFeedback: null,
});

export const formatFileSize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

export const getVideoUrl = (videoFilename) => {
  if (!videoFilename) return null;
  return `${config.API_BASE_URL}/uploads/videos/curriculum/${videoFilename}`;
};

export const validateTitle = (title) => {
  const trimmed = title.trim();
  if (!trimmed) return 'Please enter a title.';
  if (trimmed.length > TITLE_MAX_LENGTH) return `Title must be ${TITLE_MAX_LENGTH} characters or less.`;
  return null;
};

export const getAdminSessionInfo = () => ({
  email: localStorage.getItem('adminEmail') || 'admin@richuel.com',
  fullName: localStorage.getItem(config.STORAGE_KEYS.NAME) || 'Admin',
});

export const titlesFromVideos = (videos) => {
  const titles = {};
  videos.forEach((video) => {
    const position = Number(video.position);
    if (position) titles[position] = video.title || '';
  });
  return titles;
};

export const upsertVideoByPosition = (videos, nextVideo) => {
  const position = Number(nextVideo.position);
  const next = [...videos];
  const existingIndex = next.findIndex((video) => Number(video.position) === position);

  if (existingIndex >= 0) {
    next[existingIndex] = { ...next[existingIndex], ...nextVideo };
  } else {
    next.push(nextVideo);
  }

  return next.sort((a, b) => Number(a.position) - Number(b.position));
};

export const BackIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </svg>
);

export const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden>
    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
  </svg>
);

export const ChevronIcon = ({ expanded }) => (
  <svg
    className={`${styles['chevron-icon']} ${expanded ? styles['chevron-icon-expanded'] : ''}`}
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export function ButtonSpinner() {
  return <span className={styles['button-spinner']} aria-hidden />;
}

export function CurriculumLoadingState() {
  return (
    <div className={styles['curriculum-loading-shell']} aria-busy="true" aria-live="polite">
      <div className={loadingStyles['loading-container']}>
        <div className={loadingStyles['loading-spinner']}>
          <div className={loadingStyles['spinner-ring']} />
          <div className={loadingStyles['spinner-ring']} />
          <div className={loadingStyles['spinner-ring']} />
        </div>
        <p>Loading curriculum videos…</p>
      </div>
    </div>
  );
}

export function FeedbackMessage({ feedback }) {
  if (!feedback) return null;

  return (
    <div
      className={`${styles['api-message']} ${styles[`api-message-${feedback.type}`]}`}
      role={feedback.type === 'error' ? 'alert' : 'status'}
    >
      {feedback.message}
    </div>
  );
}

export function AdminCurriculumHeader({ adminInfo, onBack, onLogout }) {
  return (
    <header className={styles['admin-header']} role="banner">
      <div className={styles['header-left']}>
        <BrandLogo decorative decoding="async" to="/admin-dashboard" />
        <div className={styles['header-info']}>
          <h1 className={styles['page-title']}>Curriculum Video</h1>
          <p className={styles['page-description']}>
            Manage general curriculum videos shared across all users
          </p>
        </div>
      </div>

      <div className={styles['header-right']}>
        <div className={styles['admin-info']}>
          <div className={styles['admin-details']}>
            <p className={styles['admin-name']}>{adminInfo.fullName}</p>
            <span className={styles['admin-details-sep']} aria-hidden="true" />
            <p className={styles['admin-email']}>{adminInfo.email}</p>
          </div>
          <button
            type="button"
            className={styles['users-button']}
            onClick={onBack}
            aria-label="Back to onboarding users"
          >
            <BackIcon />
            Users
          </button>
          <button type="button" className={styles['logout-button']} onClick={onLogout}>
            <LogoutIcon />
            Log out
          </button>
        </div>
      </div>
    </header>
  );
}
